import React, { Fragment, useEffect, useRef, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import { GetAreas } from "../../../../api/masters/areaAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";
import { GetLastEntry } from "../../../../api/common/common";
import Select from "react-select";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetOpdEntries } from "../../../../api/opd/opdEntryAPI";
import { GetRadiologyTests } from "../../../../api/diagnosis/master/radiologyTestAPI";
import { GetPathologyTests } from "../../../../api/diagnosis/master/pathologyTestAPI";
import { GetDepartmentalSubSpecialities } from "../../../../api/diagnosis/master/departmentalSubSpecilityAPI";
import { StoreLabRegistration } from "../../../../api/diagnosis/transaction/labRegistrationAPI";

const schema = yup.object().shape({
	lab: yup.string().required("Lab is required"),
	date: yup.date().required("Date is required"),
	time: yup.string().required("Time is required"),
	labNo: yup.string().required("Lab No is required"),
	type: yup.string().required("Type is required"),

	patientName: yup.string().required("Patient name is required"),
	sex: yup.string().required("Sex is required"),
	age: yup.string().required("Age is required"),
	ageSuffix: yup.string().required("Age suffix is required"),

	areaId: yup.string().optional(),
	collactionCenter: yup.string().optional(),
	rateMode: yup.string().required("Rate mode is required"),
	referenceDoctorId: yup.string().optional(),
	consultantDoctorId: yup.string().required("Consultant doctor is required"),

	paymentMode: yup.string().required("Payment mode is required"),
	debitAccount: yup.string().optional(),

	total: yup.number().required("Total is required"),
	advance: yup.number(),
	balance: yup.number(),

	testDetails: yup.array().of(
		yup.object().shape({
			type: yup.string().required("Test type is required"),
			shortName: yup.string().required("Short name is required"),
			testId: yup.string().required("Test ID is required"),
			rate: yup.number().required("Rate is required"),
			doctorId: yup.string().optional(),
		})
	).min(1)
});

const AddLabRegistrationMultiple = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [consultants, setConsultants] = useState([]);
	const [areas, setAreas] = useState([]);

	const [ipdEntries, setIpdEntries] = useState([]);
	const [opdEntries, setOpdEntries] = useState([]);
	const [pathologyTests, setPathologyTests] = useState([]);
	const [radiologyTests, setRadiologyTests] = useState([]);
	const [filteredTests, setFilteredTests] = useState([]);

	const [selectedOpd, setSelectedOpd] = useState({});
	const [selectedIpd, setSelectedIpd] = useState({});
	const [departmentalSubSpecialities, setDepartmentalSubSpecialities] = useState([]);
	const [selectedDepartment, setSelectedDepartment] = useState(null);

	const [combinedDetails, setCombinedDetails] = useState([]);

	const {
		register,
		handleSubmit,
		control,
		setValue,
		getValues,
		watch,
		formState: { errors }
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			date: new Date().toISOString().split("T")[0],
			time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }),
		}
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "testDetails"
	});

	const testDetails = useWatch({
		control,
		name: "testDetails"
	});

	const type = watch("type");

	useEffect(() => {
		if (!testDetails || testDetails.length === 0) {
			setCombinedDetails([]);
			return;
		}
		console.log("testDetails", testDetails);

		const grouped = {};

		testDetails.forEach((test) => {
			console.log("test", test);
			if (!test.departmentalSubSpecialityId) return;

			const key = test.departmentalSubSpecialityId;
			const categoryName = test.departmentalSubSpecialityName;

			if (!grouped[key]) {
				grouped[key] = {
					type: categoryName,
					labNo: getValues("labNo"),
					total: 0,
					discountPercent: 0,
					discountAmount: 0,
					amount: 0,
					advance: 0,
					balance: 0
				};
			}

			grouped[key].total += Number(test.rate || 0);
		});

		// calculate amount & balance
		Object.values(grouped).forEach(item => {
			item.discountAmount = (item.total * item.discountPercent) / 100;
			item.amount = item.total - item.discountAmount;
			item.balance = 0;
		});

		const combinedArray = Object.values(grouped);
		setCombinedDetails(combinedArray);
	}, [testDetails]);

	useEffect(() => {
		if (!combinedDetails || combinedDetails.length === 0) {
			setValue("total", 0);
			return;
		}

		const grandTotal = combinedDetails.reduce(
			(sum, item) => sum + Number(item.amount || item.total || 0),
			0
		);

		setValue("total", grandTotal);
		setValue("advance", grandTotal);
		setValue("balance", 0);

	}, [combinedDetails, setValue]);

	const fetchConsultants = async () => {
		try {
			const response = await GetConsultants();
			if (response.success) {
				setConsultants(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchAreas = async () => {
		try {
			const response = await GetAreas();
			if (response.success) {
				setAreas(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchOpdEntries = async () => {
		try {
			const response = await GetOpdEntries();
			if (response.success) {
				setOpdEntries(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchIpdEntries = async () => {
		try {
			const response = await GetIpdEntries();
			if (response.success) {
				setIpdEntries(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};


	const fetchPathologyTests = async () => {
		try {
			const response = await GetPathologyTests();
			if (response.success) {
				setPathologyTests(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchRadiologyTests = async () => {
		try {
			const response = await GetRadiologyTests();
			if (response.success) {
				setRadiologyTests(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchDepartments = async () => {
		try {
			const response = await GetDepartmentalSubSpecialities();
			if (response.success) {
				setDepartmentalSubSpecialities(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchLastEntry = async () => {
		try {
			const response = await GetLastEntry();
			if (response.success) {
				setValue(
					"labNo",
					response?.data?.lastLabRegistration?.labNo ? response.data.lastLabRegistration.labNo + 1 : 1
				);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	useEffect(() => {
		fetchConsultants();
		fetchAreas();
		fetchOpdEntries();
		fetchIpdEntries();
		fetchPathologyTests();
		fetchRadiologyTests();
		fetchDepartments();
		fetchLastEntry();
	}, []);

	useEffect(() => {
		if (type === "OPD") {
			setSelectedOpd({});
		} else if (type === "IPD") {
			setSelectedIpd({});
		}
	}, [type]);

	useEffect(() => {
		if (selectedOpd) {
			setValue("uhidNo", selectedOpd?.patientId?.uhidNo);
			setValue("patientName", selectedOpd?.patientId?.patientName);
			setValue("sex", selectedOpd?.patientId?.sex);
			setValue("age", selectedOpd?.patientId?.age);
			setValue("ageSuffix", selectedOpd?.patientId?.ageSuffix);
			setValue("guardianName", selectedOpd?.patientId?.guardianName);
			setValue("mobileNo", selectedOpd?.patientId?.mobileNo);
			setValue("address", selectedOpd?.patientId?.address);
			setValue("areaId", selectedOpd?.patientId?.areaId?._id);
			setValue("consultantDoctorId", selectedOpd?.consultantDoctorId?._id);
			setValue("referenceDoctorId", selectedOpd?.referenceDoctorId?._id);
		} else if (selectedIpd) {
			setValue("uhidNo", selectedIpd?.patientId?.uhidNo);
			setValue("patientName", selectedIpd?.patientId?.patientName);
			setValue("sex", selectedIpd?.patientId?.sex);
			setValue("age", selectedIpd?.patientId?.age);
			setValue("ageSuffix", selectedIpd?.patientId?.ageSuffix);
			setValue("guardianName", selectedIpd?.patientId?.guardianName);
			setValue("mobileNo", selectedIpd?.patientId?.mobileNo);
			setValue("address", selectedIpd?.patientId?.address);
			setValue("areaId", selectedIpd?.patientId?.areaId?._id);
			setValue("consultantDoctorId", selectedIpd?.consultantDoctorId?._id);
			setValue("referenceDoctorId", selectedIpd?.referenceDoctorId?._id);
		}
	}, [selectedOpd, selectedIpd]);

	const onSubmit = async data => {
		try {
			dispatch({ type: "LOADER_START" });
			const response = await StoreLabRegistration(data);

			if (response.success) {
				showToast("success", response.message);
				navigate(`/diagnosis/transaction/lab-registration-multiple/list`);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		} finally {
			dispatch({ type: "LOADER_STOP" });
		}
	};

	const formRef = useRef(null);
	useKeyboardSubmit(handleSubmit, onSubmit);
	useEnterNavigation(formRef);

	return (
		<Fragment>
			<Breadcrumb parent="Lab" title="Add Lab Registration Multiple" />

			<div className="container-fluid">
				<div className="card">
					<div className="card-header d-flex justify-content-between">
						<h5>Add Lab Registration Multiple</h5>
						<Link to={`/diagnosis/transaction/lab-registration-multiple/list`}>
							<button className="btn btn-primary" id="list">
								Lab Registration Multiple List
							</button>
							<UncontrolledTooltip target="list">
								Lab Registration Multiple List
							</UncontrolledTooltip>
						</Link>
					</div>

					<form
						ref={formRef}
						className="form theme-form"
						onSubmit={handleSubmit(onSubmit)}
					>
						<div className="card-body">

							{/* LAB INFO */}
							<div className="col-12">
								<h5 className="mt-3 mb-3 text-primary">
									Lab Information
								</h5>
							</div>
							<div className="row">
								<div className="col-md-4">
									<label>Date</label>
									<input type="date" className="form-control" {...register("date")} />
									{errors.date && <span className="text-danger">{errors.date.message}</span>}
								</div>

								<div className="col-md-4">
									<label>Time</label>
									<input type="time" className="form-control" {...register("time")} />
									{errors.time && <span className="text-danger">{errors.time.message}</span>}
								</div>
								<div className="col-md-4">
									<label>Lab No</label>
									<input type="number" className="form-control" {...register("labNo")} readOnly />
									{errors.labNo && <span className="text-danger">{errors.labNo.message}</span>}
								</div>
							</div>

							<div className="row mt-3">
								<div className="col-md-4">
									<label>Type</label>
									<select type="number" className="form-control"
										{...register("type")}
									>
										<option value="">Select</option>
										<option>OPD</option>
										<option>IPD</option>
										<option>General</option>
									</select>
									{errors.type && <span className="text-danger">{errors.type.message}</span>}
								</div>

								{
									type === "OPD" &&
									<div className="col-md-4">
										<label>OPD No</label>
										<Select
											options={opdEntries.map((opd) => ({
												value: opd._id,
												label: `${opd.opdNo} - ${opd.patientId?.patientName}`,
												opd: opd, // store full object
											}))}
											placeholder="Search OPD..."
											isClearable
											onChange={(selectedOption) => {
												if (!selectedOption) {
													setValue("opdEntryId", "");
													return;
												}

												// set id in form
												setValue("opdEntryId", selectedOption.opd?._id);
												setValue("opdNo", selectedOption.opd?.opdNo);
												setSelectedOpd(selectedOption.opd);
												console.log(selectedOption);
											}}
										/>
										{errors.opdNo && <span className="text-danger">{errors.opdNo.message}</span>}
									</div>
								}
								{
									type === "IPD" &&
									<>
										<div className="col-md-4">
											<label>IPD No</label>
											<Select
												options={ipdEntries.map((ipd) => ({
													value: ipd._id,
													label: `${ipd.ipdNo} - ${ipd.patientId?.patientName}`,
													ipd: ipd, // store full object
												}))}
												placeholder="Search IPD..."
												isClearable
												onChange={(selectedOption) => {
													if (!selectedOption) {
														setValue("ipdEntryId", "");
														return;
													}

													// set id in form
													setValue("ipdEntryId", selectedOption.ipd?._id);
													setValue("roomNo", selectedOption.ipd?.roomId?.roomNo);

													setSelectedIpd(selectedOption.ipd);
												}}
											/>
											{errors.ipdNo && <span className="text-danger">{errors.ipdNo.message}</span>}
										</div>
										<div className="col-md-4">
											<label>Room No</label>
											<input className="form-control" {...register("roomNo")} readOnly />
											{errors.roomNo && <span className="text-danger">{errors.roomNo.message}</span>}
										</div>
									</>
								}

								{
									(type === "OPD" || type === "IPD") &&
									<div className="col-md-4">
										<label>UHID No</label>
										<input type="text" className="form-control" {...register("uhidNo")} />
										{errors.uhidNo && <span className="text-danger">{errors.uhidNo.message}</span>}
									</div>
								}


							</div>

							<hr />

							{/* PATIENT INFO */}
							<div className="col-12">
								<h5 className="mt-3 mb-3 text-primary">
									Patient Information
								</h5>
							</div>
							<div className="row">
								<div className="col-md-4">
									<label>Patient Name</label>
									<input className="form-control" {...register("patientName")}
										readOnly={(type === "OPD" || type === "IPD") ? true : false}
									/>
									{errors.patientName && <span className="text-danger">{errors.patientName.message}</span>}
								</div>

								<div className="col-md-4">
									<label>Sex</label>
									<select className="form-control" {...register("sex")}>
										<option value="">Select</option>
										<option>Male</option>
										<option>Female</option>
									</select>
									{errors.sex && <span className="text-danger">{errors.sex.message}</span>}
								</div>

								<div className="col-md-4">
									<label>Age</label>
									<div className="input-group">
										<input
											type="number"
											className="form-control"
											placeholder="Age"
											{...register("age")}
										/>
										<select
											className="form-select"
											{...register("ageSuffix")}
										>
											<option value="Years">Years</option>
											<option value="Months">Months</option>
											<option value="Days">Days</option>
										</select>
									</div>
									{errors.age && <span className="text-danger">{errors.age.message}</span>}
									{errors.ageSuffix && <span className="text-danger">{errors.ageSuffix.message}</span>}
								</div>
							</div>

							<div className="row mt-3">
								<div className="col-md-4">
									<label>Guardian Name</label>
									<input className="form-control" {...register("guardianName")} />
									{errors.guardianName && <span className="text-danger">{errors.guardianName.message}</span>}
								</div>

								<div className="col-md-4">
									<label>Mobile</label>
									<input className="form-control" {...register("mobileNo")} />
									{errors.mobileNo && <span className="text-danger">{errors.mobileNo.message}</span>}
								</div>
								<div className="col-md-4">
									<label>Address</label>
									<textarea className="form-control" {...register("address")} />
									{errors.address && <span className="text-danger">{errors.address.message}</span>}
								</div>
							</div>

							<hr />
							<div className="row mt-3">
								<div className="col-md-4">
									<label>Area</label>
									<select className="form-control" {...register("areaId")}>
										<option value="">Select</option>
										{
											areas.map((item, index) => (
												<option key={index} value={item._id}>{item.name}</option>
											))
										}
									</select>
									{errors.areaId && <span className="text-danger">{errors.areaId.message}</span>}
								</div>

								<div className="col-md-4">
									<label>Reference Doctor</label>
									<select className="form-control" {...register("referenceDoctorId")}>
										<option value="">Select</option>
										{
											consultants.map((item, index) => (
												<option key={index} value={item._id}>{item.name}</option>
											))
										}
									</select>
									{errors.referenceDoctorId && <span className="text-danger">{errors.referenceDoctorId.message}</span>}
								</div>
								<div className="col-md-4">
									<label>Consultant Doctor</label>
									<select className="form-control" {...register("consultantDoctorId")}>
										<option value="">Select</option>
										{
											consultants.map((item, index) => (
												<option key={index} value={item._id}>{item.name}</option>
											))
										}
									</select>
									{errors.consultantDoctorId && <span className="text-danger">{errors.consultantDoctorId.message}</span>}
								</div>
							</div>

							<div className="row mt-3">
								<div className="col-md-4">
									<label>Collaction Center</label>
									<input className="form-control" {...register("collactionCenter")} />
									{errors.collactionCenter && <span className="text-danger">{errors.collactionCenter.message}</span>}
								</div>
								<div className="col-md-4">
									<label>Rate Mode</label>
									<select className="form-control" {...register("rateMode")}>
										<option>Normal</option>
										<option>International</option>
									</select>
									{errors.rateMode && <span className="text-danger">{errors.rateMode.message}</span>}
								</div>
							</div>
							<div className="row mt-3">
								<div className="col-md-4">
									<label>Lab</label>
									<Select
										options={departmentalSubSpecialities.map((dept) => ({
											value: dept._id,
											label: dept.name,
											dept: dept
										}))}
										placeholder="Select Department..."
										isClearable
										onChange={async (selectedOption) => {
											if (!selectedOption) {
												setSelectedDepartment(null);
												setFilteredTests([]);
												return;
											}

											setSelectedDepartment(selectedOption.dept);
											setValue("lab", selectedOption.value);
											setFilteredTests(pathologyTests.filter(pathologyTest => pathologyTest?.investigationCategoryId?.departmentalSubSpecialityId?._id === selectedOption.value));
										}}
									/>
								</div>
								<div className="col-md-4">
									<label>Payment Mode</label>
									<select className="form-control" {...register("paymentMode")}>
										<option value="cash">Cash</option>
										<option value="online">Online</option>
									</select>
									{errors.paymentMode && <span className="text-danger">{errors.paymentMode.message}</span>}
								</div>
								<div className="col-md-4">
									<label>Debit Account</label>
									<input className="form-control" {...register("debitAccount")} />
									{errors.debitAccount && <span className="text-danger">{errors.debitAccount.message}</span>}
								</div>
							</div>

							{/* TEST DETAILS */}
							<div className="col-12">
								<h5 className="mt-3 mb-3 text-primary">
									Test Details
								</h5>
							</div>
							{fields.map((item, index) => (
								<div className="row border p-2 mb-2" key={item.id}>
									<div className="col-md-2">
										<label>Type</label>
										<select className="form-control"
											{...register(`testDetails.${index}.type`)} >
											<option value="Test">Test</option>
											<option value="Panel">Panel</option>
										</select>
									</div>

									<div className="col-md-3">
										<label>Test</label>
										<Select
											options={filteredTests.map((patho) => ({
												value: patho._id,
												label: patho.name,
												patho: patho, // store full object
											}))}
											placeholder="Search Test..."
											isClearable
											onChange={(selectedOption) => {
												if (!selectedOption) {
													setValue(`testDetails.${index}.testId`, "");
													return;
												}
												console.log(selectedOption);
												setValue(`testDetails.${index}.shortName`, selectedOption.patho.shortName);
												setValue(`testDetails.${index}.rate`, selectedOption.patho.price);
												setValue(`testDetails.${index}.departmentalSubSpecialityId`, selectedOption.patho?.investigationCategoryId?.departmentalSubSpecialityId?._id);
												setValue(`testDetails.${index}.departmentalSubSpecialityName`, selectedOption.patho?.investigationCategoryId?.departmentalSubSpecialityId?.name);
												// set id in form
												setValue(`testDetails.${index}.testId`, selectedOption.value);
												if (selectedOption.patho.rateEditable == 'Yes') {
													setValue(`testDetails.${index}.rateEditable`, true);
												} else {
													setValue(`testDetails.${index}.rateEditable`, false);
												}
											}}
										/>
									</div>

									<div className="col-md-2">
										<label>Short Name</label>
										<input className="form-control"
											{...register(`testDetails.${index}.shortName`)} />
									</div>

									<div className="col-md-2">
										<label>Rate</label>
										<input type="number" className="form-control"
											readOnly={!testDetails[index]?.rateEditable ? true : false}
											{...register(`testDetails.${index}.rate`)} />
									</div>

									<div className="col-md-2">
										<label>Doctor</label>
										<select className="form-control"
											{...register(`testDetails.${index}.doctorId`)} >
											<option value="">Select</option>
											{consultants.map((item) => (
												<option key={item._id} value={item._id}>
													{item.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-md-1 mt-4">
										<button
											type="button"
											className="btn btn-danger"
											onClick={() => remove(index)}
										>
											X
										</button>
									</div>
								</div>
							))}

							<button
								type="button"
								className="btn btn-secondary"
								onClick={() => {
									if (selectedDepartment) {
										append({
											type: "Test",
											testId: "",
											shortName: "",
											rate: "",
											doctorId: "",
											departmentalSubSpecialityId: selectedDepartment?._id || null,
											departmentalSubSpecialityName: selectedDepartment?.name || null,
											rateEditable: false
										})
									} else {
										showToast("error", "Please select department first");
									}
								}
								}
							>
								+ Add Test
							</button>

							<hr />

							<h5 className="mt-4 text-primary">Category Wise Summary</h5>

							{combinedDetails?.map((item, index) => (
								<div className="row border p-2 mb-2" key={index}>

									<div className="col-md">
										<label>Type</label>
										<input
											className="form-control"
											value={item.type}
											disabled
										/>
									</div>
									<div className="col-md">
										<label>Lab</label>
										<input
											className="form-control"
											value={item.labNo}
											disabled
										/>
									</div>

									<div className="col-md">
										<label>Total</label>
										<input
											className="form-control"
											value={item.total}
											disabled
										/>
									</div>

									<div className="col-md">
										<label>Discount %</label>
										<input
											type="number"
											className="form-control"
											value={item.discountPercent}
											disabled
										/>
									</div>

									<div className="col-md">
										<label>Disc. Amt</label>
										<input
											type="number"
											className="form-control"
											value={item.discountAmount}
											disabled
										/>
									</div>
									<div className="col-md">
										<label>Disc. Given By</label>
										<input
											type="number"
											className="form-control"
											value={item.discountGivenBy}
											disabled
										/>
									</div>
									<div className="col-md">
										<label>Disc. Doc</label>
										<input
											type="number"
											className="form-control"
											value={item.discountGivenByDoc}
											disabled
										/>
									</div>
									<div className="col-md">
										<label>Amount</label>
										<input
											type="number"
											className="form-control"
											value={item.amount}
											disabled
										/>
									</div>
									<div className="col-md">
										<label>Advance</label>
										<input
											type="number"
											className="form-control"
											value={item.advance}
											disabled
										/>
									</div>
									<div className="col-md">
										<label>Balance</label>
										<input
											type="number"
											className="form-control"
											value={item.balance}
											disabled
										/>
									</div>
								</div>
							))}

							<div className="col-12">
								<h5 className="mt-3 mb-3 text-primary">
									Billing Summary
								</h5>
							</div>
							<div className="row">
								<div className="col-md-4">
									<label>Total</label>
									<input type="number" className="form-control" {...register("total")} readOnly />
									{errors.total && <span className="text-danger">{errors.total.message}</span>}
								</div>
								<div className="col-md-4">
									<label>Advance</label>
									<input type="number" className="form-control" {...register("advance")} readOnly />
									{errors.advance && <span className="text-danger">{errors.advance.message}</span>}
								</div>
								<div className="col-md-4">
									<label>Balance</label>
									<input type="number" className="form-control" {...register("balance")} readOnly />
									{errors.balance && <span className="text-danger">{errors.balance.message}</span>}
								</div>
							</div>
						</div>

						<div className="card-footer text-end">
							<button className="btn btn-primary" type="submit">
								Submit
							</button>
						</div>
					</form>
				</div>
			</div>
		</Fragment>
	);
};

export default AddLabRegistrationMultiple;
